import type {HeaderBiddingConfig} from "header-bidding-client-base/modules/config/domain/headerBiddingConfig";
import type {GlobalConfig} from "header-bidding-client-base/modules/config/domain/globalConfig";
import type {ILogger} from "ads-client-base/modules/logger/domain/iLogger";
import {IHeaderBiddingPlatform, VideoInstreamBidData} from "./headerBiddingPlatform";
import {AmazonTamAdapter} from "header-bidding-client-base/modules/header-bidding/infrastructure/amazon-aps/amazonTamAdapter";
import {PrebidJSAdapter} from "header-bidding-client-base/modules/header-bidding/infrastructure/prebid-js/prebidJSAdapter";
import type {PrebidJsBaseBiddersLoader} from "../infrastructure/prebid-js/bidders/prebidJsBaseBiddersLoader";
import {HeaderBiddingPlatforms} from "header-bidding-base/modules/header-bidding/domain/platforms";
import type {IAdUnitTypeDTO, IAdUnitTypes} from "services-comun-meteored/modules/services/header-bidding/interface";

enum AdunitStatus {
    ADDED,
    NOT_ADDED_BY_CONFIG,
}

export type AdUnits = { [adUnitId: string]: {
    adUnitPath: string,
    adUnitType: IAdUnitTypes;
    status: AdunitStatus
}}

interface RequestManager {
    adserverRequestSent: boolean;
    platforms: {[key: string]: boolean};
    bidsData: { videoInstreamBidsData: VideoInstreamBidData[] };
}

export class HeaderBiddingService {
    private headerBiddingAdapters: IHeaderBiddingPlatform<unknown>[];
    private adUnits: AdUnits;
    private disabledBidders?: { [bidder: string]: string[] }
    private disabledAdUnits: string[];

    public constructor(private config: HeaderBiddingConfig,
                       private globalConfig: GlobalConfig,
                       private logger: ILogger,
                       private prebidJSBiddersLoader?: PrebidJsBaseBiddersLoader
    ) {
        this.adUnits = {};
        this.headerBiddingAdapters = [];
        this.disabledAdUnits = [];
    }

    public async init() {

        this.headerBiddingAdapters = this.config.platformsEnabled.map((platform) => {
            switch (platform) {
                case HeaderBiddingPlatforms.AMAZON_TAM:
                    return new AmazonTamAdapter(this.config, this.logger);
                case HeaderBiddingPlatforms.PREBID:
                    if (!this.prebidJSBiddersLoader) {
                        throw new Error("PrebidJSBiddersLoader not provided");
                    }
                    return new PrebidJSAdapter(this.config, this.globalConfig, this.logger, this.adUnits, this.prebidJSBiddersLoader);
                default:
                    throw new Error(`Platform ${platform} not supported`);
            }
        });

        await Promise.all(this.headerBiddingAdapters.map(async (adapter) => {
            try {
                await adapter.init();
            } catch (err) {
                this.logger.error(`Error initializing ${adapter.platform}`, err);
                this.headerBiddingAdapters = this.headerBiddingAdapters.filter((adapterFilter) => adapterFilter.platform !== adapter.platform);
            }
        }));

    }

    public async addAdUnits(adUnits: { adUnitPath: string, divId: string, sizes?: number[][] }[]) {

        const adUnitsParsed: { id: number, adUnitPath: string, divId: string, disabledPlatforms?: string[], type: IAdUnitTypeDTO }[] = adUnits.map((adUnit) => {
            let adUnitMapped = this.config.adUnits.find(adUnitMap => adUnitMap.adUnitPath && adUnit.adUnitPath === adUnitMap.adUnitPath);
            if (!adUnitMapped) {
                adUnitMapped = this.config.adUnits.find(adUnitMap => adUnitMap.divId && adUnit.divId === adUnitMap.divId);
            }
            if (!adUnitMapped || this.disabledAdUnits.includes(adUnit.divId)) {
                if (!adUnitMapped) this.logger.warn("Adunit not found in config", adUnit.adUnitPath, adUnit.divId);
                else this.logger.warn("Adunit disabled", adUnit.adUnitPath, adUnit.divId);
                this.adUnits[adUnit.divId] = {
                    adUnitPath: adUnit.adUnitPath,
                    adUnitType: 'banner',
                    status: AdunitStatus.NOT_ADDED_BY_CONFIG
                };
                return;
            }

            let sizes;
            if (adUnitMapped.type.banner) {
                sizes = adUnitMapped.type.banner?.sizes || adUnit.sizes;
                if (sizes && adUnitMapped.type.banner.disabledSizes && adUnitMapped.type.banner.disabledSizes.length) {
                    sizes = sizes.filter(size => !adUnitMapped.type.banner?.disabledSizes?.some(disabledSize => disabledSize[0] === size[0] && disabledSize[1] === size[1]));
                }
                if (!sizes) {
                    this.logger.warn("[Prebid.js]", "Adunit sizes not found", adUnit.adUnitPath);
                    return;
                }
                adUnitMapped.type.banner.sizes = sizes;
            }

            this.adUnits[adUnit.divId] = {
                adUnitPath: adUnit.adUnitPath,
                adUnitType: adUnitMapped.type.banner ? 'banner' : 'video',
                status: AdunitStatus.ADDED
            };

            return {
                id: adUnitMapped.id,
                adUnitPath: adUnit.adUnitPath,
                divId: adUnit.divId,
                disabledPlatforms: adUnitMapped.disabledPlatforms,
                type: adUnitMapped.type
            }
        }).filter(adUnit => !!adUnit) as { id: number, adUnitPath: string, divId: string, disabledPlatforms?: string[], type: IAdUnitTypeDTO }[];

        await Promise.allSettled(this.headerBiddingAdapters.map((adapter) => {
            adapter.addAdUnits(adUnitsParsed.filter(adUnit => !adUnit.disabledPlatforms || !adUnit.disabledPlatforms.includes(adapter.platform)));
        }));
    }

    public getAdUnitPath(adUnitId: string) {
        return this.adUnits[adUnitId];
    }

    public async removeAdUnits(adUnitIds: string[]) {
        adUnitIds.forEach((adUnitId) => {
            delete this.adUnits[adUnitId];
        });
        await Promise.allSettled(this.headerBiddingAdapters.map((adapter) => adapter.removeAdUnits(adUnitIds)));
    }

    public async requestBids(callback: (err: any, bidsData?: { videoInstreamBidsData: VideoInstreamBidData[] }) => void, adUnitCodes: string[]) {

        try {

            if (adUnitCodes.length > 0) {
                adUnitCodes = this.filterAddedAdUnits(adUnitCodes);
                if (adUnitCodes.length === 0) {
                    this.logger.warn("No adunits added by config");
                    return callback(false);
                }
            }

            const requestManager = this.createRequestManager();

            const failsafeTimeoutId = setTimeout(() => {
                this.sendBidsToAdServer(requestManager, callback, failsafeTimeoutId, true);
            }, this.config.failsafeTimeout);

            await Promise.allSettled(this.headerBiddingAdapters.map((adapter) => {
                adapter.requestBids(adUnitCodes, this.disabledBidders).then((bidsData) => {
                    this.logger.log(`${adapter.platform} request bids finsihed`);
                    requestManager.platforms[adapter.platform] = true;
                    if (bidsData?.videoInstreamBidsData) {
                        bidsData.videoInstreamBidsData.forEach((bidData) => {
                            const insertedAdUnit = requestManager.bidsData.videoInstreamBidsData.findIndex((bid) => bid.adUnitCode === bidData.adUnitCode);
                            if (insertedAdUnit === -1) {
                                requestManager.bidsData.videoInstreamBidsData.push(bidData);
                            } else {
                                if (bidData.state !== 'bid') return;
                                requestManager.bidsData.videoInstreamBidsData[insertedAdUnit].state = 'bid';
                                requestManager.bidsData.videoInstreamBidsData[insertedAdUnit].data = {
                                    ...requestManager.bidsData.videoInstreamBidsData[insertedAdUnit].data,
                                    bidderCode: bidData.data?.bidderCode || '',
                                    vastUrl: bidData.data?.vastUrl ? bidData.data.vastUrl : requestManager.bidsData.videoInstreamBidsData[insertedAdUnit].data?.vastUrl,
                                    adServerTargeting: {
                                        ...requestManager.bidsData.videoInstreamBidsData[insertedAdUnit].data?.adServerTargeting,
                                        ...bidData.data?.adServerTargeting
                                    }
                                }
                            }
                        });
                    }
                    this.sendBidsToAdServer(requestManager, callback, failsafeTimeoutId, false);
                }).catch((err) => {
                    this.logger.log(`${adapter.platform} request bids finsihed`);
                    requestManager.platforms[adapter.platform] = true;
                    this.sendBidsToAdServer(requestManager, callback, failsafeTimeoutId);
                });
            }));
        } catch (err) {
            return callback(err);
        }
    }

    public filterAddedAdUnits(adUnitCodes: string[]) {
        return adUnitCodes.filter((adUnitCode) => this.adUnits[adUnitCode]?.status === AdunitStatus.ADDED);
    }

    public disableBidder(bidder: string, slotIds: string[]): void {
        if (!this.disabledBidders) {
            this.disabledBidders = {};
        }
        if (!this.disabledBidders[bidder]) {
            this.disabledBidders[bidder] = [];
        }
        this.disabledBidders[bidder] = [...new Set([...this.disabledBidders[bidder], ...slotIds])];
    }

    public enableBidder(bidder: string, slotIds: string[]): void {
        if (!this.disabledBidders) {
            this.disabledBidders = {};
        }
        if (!this.disabledBidders[bidder]) {
            this.disabledBidders[bidder] = [];
        }
        this.disabledBidders[bidder] = this.disabledBidders[bidder].filter((slotId) => !slotIds.includes(slotId));
    }

    public disableAdUnits(adUnitIds: string[]) {
        this.disabledAdUnits = [...new Set([...this.disabledAdUnits, ...adUnitIds])];
        this.removeAdUnits(adUnitIds).then((res) => {
            return;
        });
    }

    public enableAdUnits(adUnitIds: string[]): void {
        this.disabledAdUnits = this.disabledAdUnits.filter((adUnitId) => !adUnitIds.includes(adUnitId));
        this.removeAdUnits(adUnitIds).then((res) => {
            return;
        });
    }

    public getAdSizes(adUnitId: string, platform: HeaderBiddingPlatforms) {
        switch (platform) {
            case 'prebid':
                const prebidAdapter = this.headerBiddingAdapters.find((adapter) => adapter.platform === HeaderBiddingPlatforms.PREBID);
                if (prebidAdapter) {
                    return prebidAdapter.getAdSizes(adUnitId);
                }
                break;

            case 'amazon_tam':
                const amazonTamAdapter = this.headerBiddingAdapters.find((adapter) => adapter.platform === HeaderBiddingPlatforms.AMAZON_TAM);
                if (amazonTamAdapter) {
                    return amazonTamAdapter.getAdSizes(adUnitId);
                }
                break;
        }

        return undefined;
    }

    private createRequestManager(): RequestManager {
        const requestManager: RequestManager = {
            adserverRequestSent: false,
            bidsData: { videoInstreamBidsData: [] },
            platforms: {}
        };

        this.headerBiddingAdapters.forEach((adapter) => {
            requestManager.platforms[adapter.platform] = false;
        });

        return requestManager;
    }

    private sendBidsToAdServer = (requestManager: RequestManager, callback: (err: any, bidsData?: { videoInstreamBidsData: VideoInstreamBidData[] }) => void, failsafeTimeoutId: any, isFailsafe = false) => {
        if (requestManager.adserverRequestSent === true) return;

        if (isFailsafe) {
            this.logger.warn("Adserver resumed by failsafe: " + Object.values(requestManager.platforms).filter((platform) => platform).length + " platforms responded");
            requestManager.adserverRequestSent = true;
            clearTimeout(failsafeTimeoutId);
            return callback(false, requestManager.bidsData);
        }

        if (Object.values(requestManager.platforms).every((platform) => platform)) {
            this.logger.log("All platforms responded, sending bids to adserver");
            requestManager.adserverRequestSent = true;
            clearTimeout(failsafeTimeoutId);
            return callback(false, requestManager.bidsData);
        }
    };
}
